import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FileSpreadsheet, FileText } from "lucide-react";
import { exportClinicOrdersToExcel, exportClinicOrdersToPDF } from "../lib/exportUtils";
import { APIURL, TZ } from "../../Global";
import "../styles.css";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "billed", label: "Billed" },
  { value: "dispatched", label: "Dispatched" },
  { value: "completed", label: "Completed" },
  { value: "rejected", label: "Rejected" },
  { value: "undelivered", label: "Undelivered" },
  { value: "transferred", label: "Transferred" },
];

const formatClinicName = (name) => {
  if (!name) return "—";
  return name
    .toString()
    .toLowerCase()
    .split(" ")
    .map((word) => (word ? word.charAt(0).toUpperCase() + word.slice(1) : ""))
    .join(" ");
};

const ClinicOrders = () => {
  const navigate = useNavigate();
  const { clinicId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const initialStartDate = searchParams.get("start_date") || "";
  const initialEndDate = searchParams.get("end_date") || "";
  const [clinicName, setClinicName] = useState("");
  const [orders, setOrders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [startDate, setStartDate] = useState(initialStartDate);
  const [endDate, setEndDate] = useState(initialEndDate);

  const parseOrderDate = (value) => {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    const replaced = trimmed.replace(" IST", "").replace(" ", "T");
    const normalized = replaced.replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  useEffect(() => {
    const fetchClinicOrders = async () => {
      setIsLoading(true);
      setApiError(null);
      try {
        const tokenString = sessionStorage.getItem("usertoken");
        const token = tokenString ? tokenString.replace(/["]+/g, "") : null;
        const headers = token ? { Authorization: `Token ${token}` } : {};
        const dateQuery =
          startDate && endDate ? `&start_date=${startDate}&end_date=${endDate}` : "";
        const statusQuery =
          statusFilter && statusFilter !== "all" ? `&status=${statusFilter}` : "";
        const { data } = await axios.get(
          `${APIURL}/api/v1/service-provider/central-dashboard-detail/${clinicId}/?${TZ}${dateQuery}${statusQuery}`,
          { headers },
        );

        const orderList = Array.isArray(data?.order_details) ? data.order_details : [];
        setOrders(orderList);
        if (orderList?.[0]?.hospital_name) {
          setClinicName(formatClinicName(orderList[0].hospital_name));
        }
      } catch (error) {
        console.error("Failed to load clinic dashboard detail", error);
        setApiError("Unable to load order history right now.");
      } finally {
        setIsLoading(false);
      }
    };

    if (clinicId) {
      fetchClinicOrders();
    }
  }, [clinicId, startDate, endDate, statusFilter]);

  const processedOrders = useMemo(
    () =>
      orders.map((order) => {
        const parsedDate = parseOrderDate(order.date);
        const status = (order.status || "pending").toLowerCase();
        const statusClass = status.replace(/\s+/g, "-");
        return {
          id: order.order_id,
          fulfillmentPharmacy: order.fulfillment_pharmacy || "Central Pharmacy",
          status,
          statusClass,
          statusLabel: order.status || "pending",
          date: parsedDate,
          isoDate: parsedDate ? parsedDate.toISOString().split("T")[0] : "",
        };
      }),
    [orders],
  );

  // All status/date filtering is handled by the API.
  // We just display whatever the backend returns for the current filters.
  const filteredOrders = processedOrders;

  return (
    <div className="pharmacy-page">
      <div className="pharmacy-container">
        <div className="pharmacy-page-header">
          <button
            type="button"
            className="pharmacy-back-link"
            onClick={() => navigate(-1)}
          >
            ← Back to Orders
          </button>
          <h1 className="pharmacy-page-title">
            {clinicName || "Clinic Orders"}
          </h1>
          <p className="pharmacy-page-subtitle">
            {startDate && endDate
              ? `Showing orders from ${new Date(startDate).toLocaleDateString()} to ${new Date(
                  endDate,
                ).toLocaleDateString()} for this clinic`
              : "Showing the last 30 days of orders for this clinic"}
          </p>
        </div>

        <div className="pharmacy-panel">
          <div className="pharmacy-panel-header">
            <h2 className="pharmacy-panel-title">All Orders</h2>
            <div className="pharmacy-action-group">
              <button
                type="button"
                className="pharmacy-button"
                onClick={() => exportClinicOrdersToExcel(filteredOrders)}
              >
                <FileSpreadsheet className="pharmacy-button-icon" />
                Export Excel
              </button>
              <button
                type="button"
                className="pharmacy-button"
                onClick={() => exportClinicOrdersToPDF(filteredOrders, clinicName)}
              >
                <FileText className="pharmacy-button-icon" />
                Export PDF
              </button>
            </div>
          </div>

          <div className="clinic-orders-filters">
            <div className="pharmacy-filter">
              <label className="pharmacy-filter-label" htmlFor="clinic-date-filter">
                Date range
              </label>
              <div className="pharmacy-date-input">
                <input
                  id="clinic-date-filter"
                  type="date"
                  value={startDate}
                  max={endDate || undefined}
                  onChange={(event) => setStartDate(event.target.value)}
                />
                <span>to</span>
                <input
                  type="date"
                  value={endDate}
                  min={startDate || undefined}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </div>
            </div>
            <div className="pharmacy-filter">
              <label className="pharmacy-filter-label" htmlFor="clinic-status-filter">
                Status
              </label>
              <select
                id="clinic-status-filter"
                value={statusFilter}
                onChange={(event) => setStatusFilter(event.target.value)}
              >
                <option value="all">All</option>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {apiError && (
            <div style={{ color: "#d92d20", marginBottom: 16 }}>{apiError}</div>
          )}

          {isLoading ? (
            <div style={{ padding: "24px 0" }}>Loading clinic orders...</div>
          ) : (
            <div className="pharmacy-table-wrapper">
              <table className="pharmacy-table clinic-orders-table">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Date</th>
                    <th>Fulfillment Pharmacy</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrders.length === 0 ? (
                    <tr>
                      <td colSpan={4} style={{ textAlign: "center", padding: "32px 16px" }}>
                        No orders found for this clinic in the selected date range.
                      </td>
                    </tr>
                  ) : (
                    filteredOrders.map((order) => (
                      <tr key={order.id}>
                        <td>{order.id}</td>
                        <td>{order.date ? order.date.toLocaleDateString() : "—"}</td>
                        <td>{order.fulfillmentPharmacy}</td>
                        <td>
                          <span className={`status-pill status-${order.statusClass}`}>
                            {order.statusLabel}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClinicOrders;

